import React from 'react';
import PropTypes from 'prop-types';
import { FiClock, FiInfo, FiPlay, FiPause, FiPlayCircle, FiPower, FiSlash } from 'react-icons/fi';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale/pt-BR';
import {
  Card,
  CardHeader,
  JobTitle,
  StatusBadge,
  CardBody,
  InfoRow,
  InfoLabel,
  InfoValue,
  JobId,
  CardFooter,
  ActionButton
} from './JobCard.styles';

const JobCard = ({ 
  job, 
  onViewDetails, 
  onExecute, 
  onPause, 
  onResume, 
  onDisable, 
  onEnable,
  isExecuting, 
  isPausing, 
  isResuming,
  isDisabling,
  isEnabling
}) => {
  const formatNextRun = (nextRunTime) => {
    if (!nextRunTime) return 'Não agendado';
    try {
      return format(parseISO(nextRunTime), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
    } catch (error) {
      return nextRunTime;
    }
  };

  const getJobStatus = () => {
    if (job.permanently_disabled) {
      return { label: '⛔ Desabilitado', enabled: false, type: 'disabled' };
    }
    if (job.paused) {
      return { label: '⏸ Pausado', enabled: false, type: 'paused' };
    }
    return { label: '✓ Ativo', enabled: true, type: 'active' };
  };

  const status = getJobStatus();

  return (
    <Card $isDisabled={job.permanently_disabled}>
      <CardHeader>
        <JobTitle>{job.name}</JobTitle>
        <StatusBadge $enabled={status.enabled} $type={status.type}>
          {status.label}
        </StatusBadge>
      </CardHeader>

      <CardBody>
        <InfoRow>
          <JobId>{job.id}</JobId>
        </InfoRow>

        <InfoRow>
          <FiClock size={16} />
          <InfoLabel>Próxima exec.:</InfoLabel>
          <InfoValue>{formatNextRun(job.next_run_time)}</InfoValue>
        </InfoRow>

        <InfoRow>
          <FiClock size={16} />
          <InfoLabel>Gatilho:</InfoLabel>
          <InfoValue>{job.trigger}</InfoValue>
        </InfoRow>
      </CardBody>

      <CardFooter>
        <ActionButton
          $variant="details"
          onClick={() => onViewDetails(job.id)}
          title="Ver detalhes completos do job"
        >
          <FiInfo size={14} />
          Detalhes
        </ActionButton>

        <ActionButton
          $variant="execute"
          onClick={() => onExecute(job.id)}
          disabled={isExecuting || job.permanently_disabled}
          title={job.permanently_disabled ? "Job desabilitado permanentemente" : "Executar job imediatamente"}
        >
          <FiPlay size={14} />
          {isExecuting ? 'Executando...' : 'Executar'}
        </ActionButton>

        {job.permanently_disabled ? (
          <ActionButton
            $variant="enable"
            onClick={() => onEnable(job.id)}
            disabled={isEnabling}
            title="Habilitar job permanentemente"
          >
            <FiPower size={14} />
            {isEnabling ? 'Habilitando...' : 'Habilitar'}
          </ActionButton>
        ) : job.paused ? (
          <>
            <ActionButton
              $variant="resume"
              onClick={() => onResume(job.id)}
              disabled={isResuming}
              title="Retomar execuções agendadas (temporário)"
            >
              <FiPlayCircle size={14} />
              {isResuming ? 'Retomando...' : 'Retomar'}
            </ActionButton>
            <ActionButton
              $variant="disable"
              onClick={() => onDisable(job.id)}
              disabled={isDisabling}
              title="Desabilitar job permanentemente"
            >
              <FiSlash size={14} />
              {isDisabling ? 'Desabilitando...' : 'Desabilitar'}
            </ActionButton>
          </>
        ) : (
          <>
            <ActionButton
              $variant="pause"
              onClick={() => onPause(job.id)}
              disabled={isPausing}
              title="Pausar execuções agendadas (temporário - volta após reiniciar)"
            >
              <FiPause size={14} />
              {isPausing ? 'Pausando...' : 'Pausar'}
            </ActionButton>
            <ActionButton
              $variant="disable"
              onClick={() => onDisable(job.id)}
              disabled={isDisabling}
              title="Desabilitar job permanentemente (persiste após reiniciar)"
            >
              <FiSlash size={14} />
              {isDisabling ? 'Desabilitando...' : 'Desabilitar'}
            </ActionButton>
          </>
        )}
      </CardFooter>
    </Card>
  );
};

JobCard.propTypes = {
  job: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    next_run_time: PropTypes.string,
    trigger: PropTypes.string,
    enabled: PropTypes.bool.isRequired,
    permanently_disabled: PropTypes.bool,
    paused: PropTypes.bool,
  }).isRequired,
  onViewDetails: PropTypes.func.isRequired,
  onExecute: PropTypes.func.isRequired,
  onPause: PropTypes.func.isRequired,
  onResume: PropTypes.func.isRequired,
  onDisable: PropTypes.func.isRequired,
  onEnable: PropTypes.func.isRequired,
  isExecuting: PropTypes.bool,
  isPausing: PropTypes.bool,
  isResuming: PropTypes.bool,
  isDisabling: PropTypes.bool,
  isEnabling: PropTypes.bool,
};

JobCard.defaultProps = {
  isExecuting: false,
  isPausing: false,
  isResuming: false,
  isDisabling: false,
  isEnabling: false,
};

export default JobCard;
